import React, { useEffect, useState, useRef } from "react";
import "../CommonTable/CommonTable.scss";
import DotsMenu from "../../../assets/images/vactor/dot-menu.svg";
import {
  IconButton,
  Menu,
  MenuItem,
  Pagination,
  PaginationItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
} from "@mui/material";
import { ArrowBack, ArrowForward } from "@mui/icons-material";
import { PrimaryButton, LightButton } from "../../AllButtons/AllButtons";
import { CloudUpload } from "@mui/icons-material";
import { useAppDispatch } from "../../../Store/store";
import { deleteKnowledgeBaseUpload, downloadKnowledgeBaseUpload, getKnowledgeBaseEntries, getKnowledgeBaseLastIngestion, importKnowledgeBase } from "../../../Services/admin.service";
import { openSnackbar } from "../../../Slice/snackbarSlice";
import { useNavigate } from "react-router-dom";
import { getErrorMessage } from "../../../Utils/errorMessage";

interface KnowledgeBaseUpload {
  upload_id: number;
  file_name: string;
  uploaded_by_email: string;
}

interface MissingErrorRow {
  uploadId?: number;
  fileName: string;
  field: string;
  message: string;
  rowNumber: string;
}

const GenerateTestTable = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [tests, setTests] = useState<KnowledgeBaseUpload[]>([]);
  const [isLoadingEntries, setIsLoadingEntries] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 10;

  const [openModal, setOpenModal] = useState(false);
  const [newTest, setNewTest] = useState({
    testName: "",
    category: "PAES",
    fileName: "",
  });
  const [errors, setErrors] = useState({
    testName: "",
    category: "",
    fileName: "",
  });
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [hasMissingErrors, setHasMissingErrors] = useState(false);
  const [errorUploadIds, setErrorUploadIds] = useState<number[]>([]);
  const [errorFileNames, setErrorFileNames] = useState<string[]>([]);
  const [missingErrorRows, setMissingErrorRows] = useState<MissingErrorRow[]>([]);
  const [openMissingErrorsModal, setOpenMissingErrorsModal] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const [moreMenu, setMoreMenu] = useState<null | HTMLElement>(null);
  const [selectedId, setSelectedId] = useState<number | null>(null);

  const hasErrorValue = (value: any) => {
    if (Array.isArray(value)) {
      return value.length > 0;
    }

    if (typeof value === "string") {
      return value.trim().length > 0;
    }

    if (value && typeof value === "object") {
      return Object.keys(value).length > 0;
    }

    return Boolean(value);
  };

  const formatErrorValue = (value: any) => {
    if (value === null || value === undefined || value === "") {
      return "-";
    }

    if (typeof value === "object") {
      return JSON.stringify(value);
    }

    return String(value);
  };

  const collectErrorTargets = (value: any): { uploadIds: number[]; fileNames: string[]; hasError: boolean; rows: MissingErrorRow[] } => {
    const uploadIds = new Set<number>();
    const fileNames = new Set<string>();
    const rows: MissingErrorRow[] = [];
    let hasError = false;

    const addErrorRows = (errorValue: any, context: { uploadId?: number; fileName?: string }) => {
      const errorItems = Array.isArray(errorValue) ? errorValue : [errorValue];
      const fileName = context.fileName || "-";

      errorItems.forEach((errorItem) => {
        if (!hasErrorValue(errorItem)) {
          return;
        }

        if (errorItem && typeof errorItem === "object" && !Array.isArray(errorItem)) {
          const errorRecord = errorItem as Record<string, any>;
          rows.push({
            uploadId: context.uploadId,
            fileName,
            field: formatErrorValue(errorRecord.field),
            message: formatErrorValue(errorRecord.message ?? errorRecord.error ?? errorRecord.detail),
            rowNumber: formatErrorValue(errorRecord.row_number ?? errorRecord.rowNumber ?? errorRecord.row),
          });
          return;
        }

        rows.push({
          uploadId: context.uploadId,
          fileName,
          field: "-",
          message: formatErrorValue(errorItem),
          rowNumber: "-",
        });
      });
    };

    const visit = (item: any, context: { uploadId?: number; fileName?: string } = {}) => {
      if (item === null || item === undefined) {
        return;
      }

      if (Array.isArray(item)) {
        item.forEach((child) => visit(child, context));
        return;
      }

      if (typeof item !== "object") {
        return;
      }

      const record = item as Record<string, any>;
      const uploadIdCandidate = record.upload_id ?? record.uploadId ?? context.uploadId;
      const fileNameCandidate = record.file_name ?? record.filename ?? record.fileName ?? context.fileName;
      const nextContext = {
        uploadId: typeof uploadIdCandidate === "number" ? uploadIdCandidate : context.uploadId,
        fileName: typeof fileNameCandidate === "string" && fileNameCandidate.trim()
          ? fileNameCandidate.trim()
          : context.fileName,
      };
      const errorValue = record.error ?? record.errors ?? record.error_message ?? record.missing_errors ?? record.validation_errors;
      const hasRecordError = hasErrorValue(errorValue);

      if (hasRecordError) {
        hasError = true;

        if (nextContext.uploadId !== undefined) {
          uploadIds.add(nextContext.uploadId);
        }

        if (nextContext.fileName) {
          fileNames.add(nextContext.fileName);
        }

        addErrorRows(errorValue, nextContext);
      }

      Object.values(record).forEach((child) => visit(child, nextContext));
    };

    visit(value);

    return {
      uploadIds: Array.from(uploadIds),
      fileNames: Array.from(fileNames),
      hasError,
      rows,
    };
  };

  const fetchKnowledgeBaseEntriesList = async () => {
    setIsLoadingEntries(true);
    try {
      const uploadsResponse = await dispatch(getKnowledgeBaseEntries({ limit: 20 })).unwrap();
      const entries = Array.isArray(uploadsResponse?.uploads) ? uploadsResponse.uploads : [];

      let ingestionErrors = {
        hasError: false,
        uploadIds: [] as number[],
        fileNames: [] as string[],
        rows: [] as MissingErrorRow[],
      };

      try {
        const lastIngestionResponse = await dispatch(getKnowledgeBaseLastIngestion()).unwrap();
        ingestionErrors = collectErrorTargets(lastIngestionResponse);
      } catch (error) {
        ingestionErrors = {
          hasError: false,
          uploadIds: [],
          fileNames: [],
          rows: [],
        };
      }

      const hasErrors = ingestionErrors.hasError;
      const uploadIds = [...ingestionErrors.uploadIds];
      const fileNames = [...ingestionErrors.fileNames];
      const fileNameByUploadId = new Map<number, string>(
        entries
          .filter((entry: KnowledgeBaseUpload) => typeof entry.upload_id === "number" && Boolean(entry.file_name))
          .map((entry: KnowledgeBaseUpload): [number, string] => [entry.upload_id, entry.file_name])
      );
      let rows = [...ingestionErrors.rows];

      if (hasErrors && uploadIds.length === 0 && fileNames.length === 0 && entries.length > 0) {
        uploadIds.push(entries[0].upload_id);
        if (entries[0].file_name) {
          fileNames.push(entries[0].file_name);
          rows = rows.map((row) => ({
            ...row,
            fileName: row.fileName === "-" ? entries[0].file_name : row.fileName,
          }));
        }
      }

      const defaultFileName = fileNames.length === 1 ? fileNames[0] : "";
      rows = rows.map((row) => ({
        ...row,
        fileName: row.fileName === "-" && row.uploadId && fileNameByUploadId.has(row.uploadId)
          ? fileNameByUploadId.get(row.uploadId) || row.fileName
          : row.fileName === "-" && defaultFileName
            ? defaultFileName
            : row.fileName,
      }));
      rows = rows.filter((row, index, currentRows) => (
        currentRows.findIndex((currentRow) => (
          currentRow.fileName === row.fileName &&
          currentRow.field === row.field &&
          currentRow.message === row.message &&
          currentRow.rowNumber === row.rowNumber
        )) === index
      ));

      setTests(entries);
      setCurrentPage(1);
      setHasMissingErrors(hasErrors);
      setErrorUploadIds(uploadIds);
      setErrorFileNames(fileNames);
      setMissingErrorRows(rows);
    } catch (error: any) {
      setTests([]);
      setHasMissingErrors(false);
      setErrorUploadIds([]);
      setErrorFileNames([]);
      setMissingErrorRows([]);
      dispatch(
        openSnackbar({
          message: getErrorMessage(error, "Failed to fetch knowledge base entries."),
          severity: "error",
        })
      );
    } finally {
      setIsLoadingEntries(false);
    }
  };

  const handleOpenModal = () => setOpenModal(true);
  const handleOpenMissingErrorsModal = () => setOpenMissingErrorsModal(true);
  const handleCloseMissingErrorsModal = () => setOpenMissingErrorsModal(false);

  const handleViewUploadedFile = (test: KnowledgeBaseUpload) => {
    if (test.upload_id) {
      navigate(`/admin/view_file_uploaded/${test.upload_id}`);
    }
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setNewTest({ testName: "", category: "PAES", fileName: "" });
    setErrors({ testName: "", category: "", fileName: "" });
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleSaveTest = async () => {
    const newErrors = {
      testName: "",
      category: "",
      fileName: selectedFile ? "" : "File upload is required",
    };

    setErrors(newErrors);

    if (Object.values(newErrors).every((error) => error === "") && selectedFile) {
      setIsSaving(true);
      try {
        const response = await dispatch(
          importKnowledgeBase({ file: selectedFile })
        ).unwrap();
        await fetchKnowledgeBaseEntriesList();
        dispatch(
          openSnackbar({
            message: response?.message || "Knowledge base imported successfully.",
            severity: "success",
          })
        );
        handleCloseModal();
      } catch (error: any) {
        dispatch(
          openSnackbar({
            message: getErrorMessage(error, "Failed to import knowledge base."),
            severity: "error",
          })
        );
      } finally {
        setIsSaving(false);
      }
    }
  };

  const handleClickMoreMenu = (event: React.MouseEvent<HTMLButtonElement>, id: number) => {
    setMoreMenu(event.currentTarget);
    setSelectedId(id);
  };

  const handleCloseMoreMenu = () => {
    setMoreMenu(null);
    setSelectedId(null);
  };

  const handleOpenDeleteDialog = () => {
    setMoreMenu(null);
    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    if (isDeleting) {
      return;
    }

    setOpenDeleteDialog(false);
    setSelectedId(null);
  };

  const handleDeleteTest = async () => {
    if (selectedId !== null) {
      const selectedUpload = tests[selectedId];

      if (!selectedUpload?.upload_id) {
        handleCloseDeleteDialog();
        return;
      }

      setIsDeleting(true);
      try {
        const response = await dispatch(
          deleteKnowledgeBaseUpload({ uploadId: selectedUpload.upload_id })
        ).unwrap();

        await fetchKnowledgeBaseEntriesList();
        dispatch(
          openSnackbar({
            message: response?.message || "Knowledge base upload deleted successfully.",
            severity: "success",
          })
        );
      } catch (error: any) {
        dispatch(
          openSnackbar({
            message: getErrorMessage(error, "Failed to delete knowledge base upload."),
            severity: "error",
          })
        );
      } finally {
        setIsDeleting(false);
        setOpenDeleteDialog(false);
        setSelectedId(null);
      }
    }
  };

  const handleDownloadTest = async () => {
    if (selectedId !== null) {
      const selectedUpload = tests[selectedId];

      if (!selectedUpload?.upload_id) {
        handleCloseMoreMenu();
        return;
      }

      setIsDownloading(true);
      try {
        const response = await dispatch(
          downloadKnowledgeBaseUpload({ uploadId: selectedUpload.upload_id })
        ).unwrap();

        const blob = response.data as Blob;
        const contentDisposition = response.headers?.["content-disposition"];
        const matchedFileName = contentDisposition?.match(/filename="?([^"]+)"?/i)?.[1];
        const downloadFileName = matchedFileName || selectedUpload.file_name || `upload-${selectedUpload.upload_id}.csv`;

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = downloadFileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        dispatch(
          openSnackbar({
            message: "Knowledge base file downloaded successfully.",
            severity: "success",
          })
        );
      } catch (error: any) {
        dispatch(
          openSnackbar({
            message: getErrorMessage(error, "Failed to download knowledge base file."),
            severity: "error",
          })
        );
      } finally {
        setIsDownloading(false);
        handleCloseMoreMenu();
      }
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const allowedExtensions = ["csv", "xlsx","xls", "json"];
      const fileExtension = file.name.split(".").pop()?.toLowerCase();

      if (fileExtension && allowedExtensions.includes(fileExtension)) {
        setSelectedFile(file);
        setNewTest({ ...newTest, fileName: file.name });
        setErrors({ ...errors, fileName: "" });
      } else {
        setErrors({ ...errors, fileName: "Invalid file type. Only CSV, Excel and JSON files are allowed." });
        setSelectedFile(null);
        setNewTest({ ...newTest, fileName: "" });
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
      }
    }
  };

  const paginatedTests = tests.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );

  const totalPages = Math.ceil(tests.length / rowsPerPage);

  const isFileMarkedAsError = (test: KnowledgeBaseUpload) => {
    return errorUploadIds.includes(test.upload_id) || errorFileNames.includes(test.file_name);
  };

  const missingErrorsFileName = missingErrorRows.find((row) => row.fileName && row.fileName !== "-")?.fileName || errorFileNames[0] || "-";

  useEffect(() => {
    fetchKnowledgeBaseEntriesList();
  }, []);

  return (
    <>
      <div className="common-table-wrapper">
        <div className="user-management-table">
          <div className="common-table-filter-block">
            {hasMissingErrors && <LightButton onClick={handleOpenMissingErrorsModal} sx={{ color: "#DF1C41" }}>Missing Errors</LightButton>}
            <PrimaryButton onClick={handleOpenModal}>Upload file</PrimaryButton>
          </div>

          <TableContainer>
            <Table aria-label="document table">
              <TableHead>
                <TableRow>
                  <TableCell> Id</TableCell>
                  <TableCell>Filename</TableCell>
                  <TableCell>Uploaded By</TableCell>
                  <TableCell align="center">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoadingEntries ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      <CircularProgress size={24} />
                    </TableCell>
                  </TableRow>
                ) : paginatedTests.map((test, index) => (
                  <TableRow key={String(test.upload_id ?? index)} hover>
                    <TableCell>{test.upload_id ?? "-"}</TableCell>
                    <TableCell sx={{ color: isFileMarkedAsError(test) ? "#DF1C41" : "inherit" }}>
                      <Box
                        component="button"
                        type="button"
                        onClick={() => handleViewUploadedFile(test)}
                        sx={{
                          all: "unset",
                          color: "inherit",
                          cursor: "pointer",
                          "&:hover": {
                            textDecoration: "underline",
                          },
                        }}
                      >
                        {test.file_name || "-"}
                      </Box>
                    </TableCell>

                    <TableCell>{test.uploaded_by_email || "-"}</TableCell>
                    <TableCell align="center">
                      <IconButton onClick={(e) => handleClickMoreMenu(e, (currentPage - 1) * rowsPerPage + index)}>
                        <img src={DotsMenu} alt="more" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                {!isLoadingEntries && paginatedTests.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} align="center">No tests found</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          {!isLoadingEntries && totalPages > 1 && (
            <div className="common-table-pagination">
              <div></div>
              <Stack spacing={2}>
                <Pagination
                  variant="outlined"
                  shape="rounded"
                  count={totalPages}
                  page={currentPage}
                  onChange={(_, page) => setCurrentPage(page)}
                  renderItem={(item) => (
                    <PaginationItem
                      {...item}
                      components={{
                        previous: ArrowBack,
                        next: ArrowForward,
                      }}
                    />
                  )}
                />
              </Stack>
            </div>
          )}
        </div>
      </div>

      <Menu
        anchorEl={moreMenu}
        open={Boolean(moreMenu)}
        onClose={handleCloseMoreMenu}
      >
        <MenuItem onClick={handleOpenDeleteDialog} style={{ color: "red" }}>Delete</MenuItem>
        <MenuItem onClick={handleDownloadTest} disabled={isDownloading}>Download</MenuItem>

      </Menu>

      <Dialog open={openDeleteDialog} onClose={handleCloseDeleteDialog} fullWidth maxWidth="xs">
        <DialogTitle>Delete File</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ color: "#1F2937" }}>
            Are you sure you want to delete this file?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <LightButton onClick={handleCloseDeleteDialog} disabled={isDeleting}>Cancel</LightButton>
          <PrimaryButton onClick={handleDeleteTest} loading={isDeleting}>Yes, delete</PrimaryButton>
        </DialogActions>
      </Dialog>

      <Dialog open={openModal} onClose={handleCloseModal} fullWidth maxWidth="sm">
        <DialogTitle>Add New Test</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 2 }}>
            {/* <TextField
              label="Test Name"
              fullWidth
              value={newTest.testName}
              inputProps={{ maxLength: 50 }}
              error={!!errors.testName}
              helperText={errors.testName || `${newTest.testName.length}/50 characters`}
              onChange={(e) => {
                setNewTest({ ...newTest, testName: e.target.value });
                setErrors({ ...errors, testName: "" });
              }}
            /> */}
            {/* <FormControl fullWidth>
              <InputLabel id="category-label">Category</InputLabel>
              <Select
                labelId="category-label"
                value={newTest.category}
                label="Category"
                onChange={handleCategoryChange}
                error={!!errors.category}
              >
                {categories.map((cat) => (
                  <MenuItem key={cat} value={cat}>{cat}</MenuItem>
                ))}
              </Select>
              {errors.category && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                  {errors.category}
                </Typography>
              )}
            </FormControl> */}

            <Box
              sx={{
                border: "1px dashed #ccc",
                borderRadius: 1,
                p: 3,
                textAlign: "center",
                cursor: "pointer",
                borderColor: errors.fileName ? "red" : "#ccc",
                "&:hover": { borderColor: errors.fileName ? "red" : "#4F46E5" },
              }}
              onClick={() => fileInputRef.current?.click()}
            >
              <input
                type="file"
                hidden
                ref={fileInputRef}
                onChange={handleFileChange}
                accept=".csv,.json,.xls,.xlsx"
                disabled={isSaving}
              />
              <CloudUpload sx={{ fontSize: 40, color: "#666", mb: 1 }} />
              <Typography variant="body1" sx={{ color: "#333", fontWeight: 500 }}>
                {newTest.fileName || "Click to upload file"}
              </Typography>
              <Typography variant="caption" sx={{ color: errors.fileName ? "red" : "#666" }}>
                {errors.fileName || "Supports Excel, CSV and JSON files."}
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <LightButton onClick={handleCloseModal} disabled={isSaving}>Cancel</LightButton>
          <PrimaryButton onClick={handleSaveTest} loading={isSaving}>Upload</PrimaryButton>
        </DialogActions>
      </Dialog>

      <Dialog open={openMissingErrorsModal} onClose={handleCloseMissingErrorsModal} fullWidth maxWidth="md">
        <DialogTitle>
          <div>Missing Errors</div>
          <Typography variant="body2" sx={{ mt: 1, color: "#525866", fontWeight: 500 }}>
            File name - {missingErrorsFileName}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <TableContainer
            sx={{
              border: "1px solid #E0E0E0",
              borderRadius: 1,
              maxHeight: "60vh",
              overflowY: "auto",
              scrollbarGutter: "stable",
              "&::-webkit-scrollbar": {
                width: "8px",
              },
              "&::-webkit-scrollbar-track": {
                backgroundColor: "#F3F4F6",
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "#C4C4C4",
                borderRadius: "8px",
              },
            }}
          >
            
            <Table aria-label="missing errors table">
              <TableHead>
                <TableRow>
                  {/* <TableCell>File Name</TableCell> */}
                  <TableCell>Field</TableCell>
                  <TableCell>Message</TableCell>
                  <TableCell>Row Number</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {missingErrorRows.length > 0 ? (
                  missingErrorRows.map((errorRow, index) => (
                    <TableRow key={`${errorRow.fileName}-${errorRow.field}-${errorRow.rowNumber}-${index}`}>
                      {/* <TableCell>{errorRow.fileName}</TableCell> */}
                      <TableCell>{errorRow.field}</TableCell>
                      <TableCell>{errorRow.message}</TableCell>
                      <TableCell>{errorRow.rowNumber}</TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={4} align="center">No missing errors found</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <LightButton onClick={handleCloseMissingErrorsModal}>Close</LightButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default GenerateTestTable;
